export enum UserRoles {
  super_admin = "super-admin",
  owner = "owner",
  admin = "admin",
  user = "user",
  restricted = "restricted",
}

// Helper functions for role mapping and display
export const getRoleDisplayName = (role: string): string => {
  const roleMap: Record<string, string> = {
    [UserRoles.super_admin]: "Super Admin",
    [UserRoles.owner]: "Owner",
    [UserRoles.admin]: "Admin",
    [UserRoles.user]: "User",
    [UserRoles.restricted]: "Restricted User",
  };
  return roleMap[role] || role;
};

export const isSystemRole = (role: string): boolean => {
  return [UserRoles.owner, UserRoles.admin, UserRoles.user].includes(role as UserRoles);
};

export const isProtectedRole = (role: string): boolean => {
  return [UserRoles.owner, UserRoles.admin].includes(role as UserRoles);
};

export enum ContactFields {
  fname = "First Name",
  lname = "Last Name",
  company = "Company",
  email = "Email",
  linkedInUrl = "LinkedIn url",
  mobileNumber = "Mobile Number",
  mobilePhoneStatus = "Mobile Phone Status",
  directNumber = "Direct Number",
  directPhoneStatus = "Direct Phone Status",
  jobTitle = "Title",
  industry = "Industry",
  companyPhoneNumber = "Company Phone number",
  companyHQ = "Company HQ Address",
  website = "Website",
  companyLinkedInUrl = "Company LinkedIn url",
  companySize = "Headcount",
}

export enum TaskType {
  call = "call",
  email = "email",
  linkedIn = "linkedIn",
  whatsApp = "whatsApp",
  sms = "sms",
  other = "other",
}

export enum MovementType {
  bucketMove = "BUCKET_MOVE",
  opportunityCreated = "OPPORTUNITY_CREATED",
  opportunityStageChange = "OPPORTUNITY_STAGE_CHANGE",
}

export enum BucketFollowUpState {
  past = "past",
  due = "due",
  future = "future",
}

export enum ProductPurchaseTypes {
  oneTime = "One-Time",
  monthly = "Monthly",
  annual = "Annual",
}

export enum ProductPriceTypes {
  fixed = "fixed",
  flexible = "flexible",
}

export enum DealTypes {
  newBusiness = "New Business",
  existingBusiness = "Existing Business",
  upsell = "Upsell",
  renewal = "Renewal",
  crossSell = "Cross-Sell",
}

export enum IntegrationProviders {
  zoom = "zoom",
  google = "google",
  teams = "teams",
}

export enum MeetingStatuses {
  pending = "pending",
  noShow = "no-show",
  meetingHeld = "meeting-held",
}

export enum DialerRotationStrategy {
  default = "default",
  proximity = "proximity",
}

export enum SocketChannels {
  nofitification = "nofitification",
}

export enum NotificationType {
  info = "info",
  success = "success",
  warning = "warning",
  error = "error",
}

export enum NotificationCategory {
  enrichment = "enrichment",
  account = "account",
  meeting = "meeting",
  task = "task",
  ai_generation = "ai_generation",
  mention = "mention",
}

export interface TimezoneOption {
  value: string;
  label: string;
  cities?: string[];
}

export const TIMEZONES: TimezoneOption[] = [
  {
    value: "America/New_York",
    label: "New York — Eastern Time (ET)",
    cities: ["Boston", "Philadelphia", "Atlanta", "Miami", "Washington DC"],
  },
  {
    value: "America/Toronto",
    label: "Toronto — Eastern Time (ET)",
    cities: ["Ottawa", "Montreal", "Quebec City"],
  },
  {
    value: "America/Chicago",
    label: "Chicago — Central Time (CT)",
    cities: ["Houston", "Dallas", "Minneapolis", "New Orleans"],
  },
  {
    value: "America/Denver",
    label: "Denver — Mountain Time (MT)",
    cities: ["Salt Lake City", "Albuquerque", "Boise"],
  },
  { value: "America/Phoenix", label: "Phoenix — Mountain Standard Time (MST)", cities: ["Tucson"] },
  {
    value: "America/Los_Angeles",
    label: "Los Angeles — Pacific Time (PT)",
    cities: ["San Francisco", "Seattle", "Portland", "San Diego", "Las Vegas"],
  },
  { value: "America/Vancouver", label: "Vancouver — Pacific Time (PT)", cities: ["Victoria"] },
  { value: "America/Anchorage", label: "Anchorage — Alaska Time (AKT)", cities: [] },
  { value: "Pacific/Honolulu", label: "Honolulu — Hawaii Time (HST)", cities: ["Hawaii"] },
  {
    value: "America/Mexico_City",
    label: "Mexico City — Central Time (CT)",
    cities: ["Guadalajara", "Monterrey"],
  },
  {
    value: "America/Bogota",
    label: "Bogotá — Colombia Time (COT)",
    cities: ["Bogota", "Medellin", "Cali"],
  },
  { value: "America/Lima", label: "Lima — Peru Time (PET)", cities: ["Peru"] },
  {
    value: "America/Sao_Paulo",
    label: "São Paulo — Brasilia Time (BRT)",
    cities: ["Sao Paulo", "Rio de Janeiro", "Brasilia"],
  },
  {
    value: "America/Argentina/Buenos_Aires",
    label: "Buenos Aires — Argentina Time (ART)",
    cities: ["Córdoba"],
  },
  { value: "America/Santiago", label: "Santiago — Chile Time (CLT)", cities: ["Chile"] },
  { value: "America/Caracas", label: "Caracas — Venezuela Time (VET)", cities: ["Venezuela"] },
  { value: "America/Halifax", label: "Halifax — Atlantic Time (AT)", cities: ["Nova Scotia"] },
  {
    value: "America/St_Johns",
    label: "St. John's — Newfoundland Time (NT)",
    cities: ["Newfoundland"],
  },
  { value: "America/Panama", label: "Panama City — EST (no DST)", cities: ["Panama"] },
  {
    value: "Europe/London",
    label: "London — Greenwich Mean Time (GMT)",
    cities: ["Dublin", "Edinburgh"],
  },
  { value: "Europe/Lisbon", label: "Lisbon — Western European Time (WET)", cities: ["Porto"] },
  {
    value: "Europe/Paris",
    label: "Paris — Central European Time (CET)",
    cities: ["Lyon", "Marseille"],
  },
  {
    value: "Europe/Berlin",
    label: "Berlin — Central European Time (CET)",
    cities: ["Hamburg", "Munich", "Frankfurt", "Cologne"],
  },
  {
    value: "Europe/Rome",
    label: "Rome — Central European Time (CET)",
    cities: ["Milan", "Naples", "Turin"],
  },
  {
    value: "Europe/Madrid",
    label: "Madrid — Central European Time (CET)",
    cities: ["Barcelona", "Seville", "Valencia"],
  },
  {
    value: "Europe/Amsterdam",
    label: "Amsterdam — Central European Time (CET)",
    cities: ["Rotterdam", "The Hague"],
  },
  {
    value: "Europe/Brussels",
    label: "Brussels — Central European Time (CET)",
    cities: ["Antwerp", "Ghent"],
  },
  {
    value: "Europe/Vienna",
    label: "Vienna — Central European Time (CET)",
    cities: ["Graz", "Salzburg"],
  },
  {
    value: "Europe/Zurich",
    label: "Zurich — Central European Time (CET)",
    cities: ["Geneva", "Basel", "Bern"],
  },
  {
    value: "Europe/Stockholm",
    label: "Stockholm — Central European Time (CET)",
    cities: ["Gothenburg", "Malmö"],
  },
  {
    value: "Europe/Oslo",
    label: "Oslo — Central European Time (CET)",
    cities: ["Bergen", "Trondheim"],
  },
  {
    value: "Europe/Copenhagen",
    label: "Copenhagen — Central European Time (CET)",
    cities: ["Aarhus"],
  },
  {
    value: "Europe/Warsaw",
    label: "Warsaw — Central European Time (CET)",
    cities: ["Kraków", "Krakow"],
  },
  { value: "Europe/Prague", label: "Prague — Central European Time (CET)", cities: ["Brno"] },
  { value: "Europe/Budapest", label: "Budapest — Central European Time (CET)", cities: [] },
  {
    value: "Europe/Helsinki",
    label: "Helsinki — Eastern European Time (EET)",
    cities: ["Tallinn", "Riga"],
  },
  {
    value: "Europe/Bucharest",
    label: "Bucharest — Eastern European Time (EET)",
    cities: ["Cluj-Napoca"],
  },
  { value: "Europe/Sofia", label: "Sofia — Eastern European Time (EET)", cities: [] },
  {
    value: "Europe/Athens",
    label: "Athens — Eastern European Time (EET)",
    cities: ["Thessaloniki"],
  },
  { value: "Europe/Kiev", label: "Kyiv — Eastern European Time (EET)", cities: ["Kharkiv"] },
  { value: "Europe/Istanbul", label: "Istanbul — Turkey Time (TRT)", cities: ["Ankara", "Izmir"] },
  {
    value: "Europe/Moscow",
    label: "Moscow — Moscow Time (MSK)",
    cities: ["Saint Petersburg", "St. Petersburg"],
  },
  { value: "Africa/Cairo", label: "Cairo — Eastern European Time (EET)", cities: ["Alexandria"] },
  {
    value: "Africa/Casablanca",
    label: "Casablanca — Western European Time (WET)",
    cities: ["Rabat", "Morocco"],
  },
  { value: "Africa/Tunis", label: "Tunis — Central European Time (CET)", cities: ["Tunisia"] },
  {
    value: "Africa/Lagos",
    label: "Lagos — West Africa Time (WAT)",
    cities: ["Abuja", "Kano", "Nigeria"],
  },
  { value: "Africa/Accra", label: "Accra — Greenwich Mean Time (GMT)", cities: ["Ghana"] },
  {
    value: "Africa/Nairobi",
    label: "Nairobi — East Africa Time (EAT)",
    cities: ["Addis Ababa", "Dar es Salaam", "Kampala"],
  },
  {
    value: "Africa/Johannesburg",
    label: "Johannesburg — South Africa Time (SAST)",
    cities: ["Cape Town", "Durban", "Pretoria"],
  },
  { value: "Africa/Khartoum", label: "Khartoum — Central Africa Time (CAT)", cities: ["Sudan"] },
  {
    value: "Asia/Dubai",
    label: "Dubai — Gulf Standard Time (GST)",
    cities: ["Abu Dhabi", "Sharjah", "UAE"],
  },
  {
    value: "Asia/Riyadh",
    label: "Riyadh — Arabia Standard Time (AST)",
    cities: ["Jeddah", "Mecca", "Saudi Arabia"],
  },
  {
    value: "Asia/Jerusalem",
    label: "Tel Aviv — Israel Standard Time (IST)",
    cities: ["Jerusalem", "Haifa", "Israel"],
  },
  { value: "Asia/Tehran", label: "Tehran — Iran Standard Time (IRST)", cities: ["Iran"] },
  { value: "Asia/Beirut", label: "Beirut — Eastern European Time (EET)", cities: ["Lebanon"] },
  { value: "Asia/Amman", label: "Amman — Arabia Standard Time (AST)", cities: ["Jordan"] },
  { value: "Asia/Baghdad", label: "Baghdad — Arabia Standard Time (AST)", cities: ["Iraq"] },
  { value: "Asia/Muscat", label: "Muscat — Gulf Standard Time (GST)", cities: ["Oman"] },
  {
    value: "Asia/Karachi",
    label: "Karachi — Pakistan Standard Time (PKT)",
    cities: ["Lahore", "Islamabad", "Pakistan"],
  },
  { value: "Asia/Kabul", label: "Kabul — Afghanistan Time (AFT)", cities: ["Afghanistan"] },
  { value: "Asia/Tashkent", label: "Tashkent — Uzbekistan Time (UZT)", cities: ["Uzbekistan"] },
  { value: "Asia/Almaty", label: "Almaty — Kazakhstan Time (ALMT)", cities: ["Kazakhstan"] },
  {
    value: "Asia/Kolkata",
    label: "Mumbai — India Standard Time (IST)",
    cities: ["Delhi", "Bangalore", "Hyderabad", "Chennai", "Kolkata", "India"],
  },
  { value: "Asia/Kathmandu", label: "Kathmandu — Nepal Time (NPT)", cities: ["Nepal"] },
  { value: "Asia/Colombo", label: "Colombo — Sri Lanka Time (SLT)", cities: ["Sri Lanka"] },
  {
    value: "Asia/Dhaka",
    label: "Dhaka — Bangladesh Standard Time (BST)",
    cities: ["Chittagong", "Bangladesh"],
  },
  { value: "Asia/Yangon", label: "Yangon — Myanmar Time (MMT)", cities: ["Myanmar"] },
  {
    value: "Asia/Bangkok",
    label: "Bangkok — Indochina Time (ICT)",
    cities: ["Hanoi", "Ho Chi Minh City", "Phnom Penh", "Vietnam"],
  },
  {
    value: "Asia/Jakarta",
    label: "Jakarta — Western Indonesia Time (WIB)",
    cities: ["Surabaya", "Bandung", "Indonesia"],
  },
  { value: "Asia/Kuala_Lumpur", label: "Kuala Lumpur — Malaysia Time (MYT)", cities: ["Malaysia"] },
  { value: "Asia/Singapore", label: "Singapore — Singapore Standard Time (SGT)", cities: [] },
  {
    value: "Asia/Manila",
    label: "Manila — Philippine Time (PHT)",
    cities: ["Cebu", "Davao", "Philippines"],
  },
  {
    value: "Asia/Shanghai",
    label: "Shanghai — China Standard Time (CST)",
    cities: ["Beijing", "Guangzhou", "Shenzhen", "China"],
  },
  { value: "Asia/Hong_Kong", label: "Hong Kong — Hong Kong Time (HKT)", cities: [] },
  { value: "Asia/Taipei", label: "Taipei — Taiwan Time (CST)", cities: ["Taiwan"] },
  {
    value: "Asia/Tokyo",
    label: "Tokyo — Japan Standard Time (JST)",
    cities: ["Osaka", "Kyoto", "Yokohama", "Japan"],
  },
  {
    value: "Asia/Seoul",
    label: "Seoul — Korea Standard Time (KST)",
    cities: ["Busan", "South Korea"],
  },
  {
    value: "Australia/Sydney",
    label: "Sydney — Australian Eastern Time (AET)",
    cities: ["Canberra", "New South Wales"],
  },
  {
    value: "Australia/Melbourne",
    label: "Melbourne — Australian Eastern Time (AET)",
    cities: ["Victoria"],
  },
  {
    value: "Australia/Brisbane",
    label: "Brisbane — Australian Eastern Time (AET)",
    cities: ["Queensland"],
  },
  {
    value: "Australia/Perth",
    label: "Perth — Australian Western Time (AWT)",
    cities: ["Western Australia"],
  },
  {
    value: "Australia/Adelaide",
    label: "Adelaide — Australian Central Time (ACT)",
    cities: ["South Australia"],
  },
  {
    value: "Australia/Darwin",
    label: "Darwin — Australian Central Standard Time (ACST)",
    cities: ["Northern Territory"],
  },
  {
    value: "Pacific/Auckland",
    label: "Auckland — New Zealand Time (NZST)",
    cities: ["Wellington", "Christchurch", "New Zealand"],
  },
  { value: "Pacific/Fiji", label: "Suva — Fiji Time (FJT)", cities: ["Fiji"] },
  { value: "Pacific/Guam", label: "Guam — Chamorro Standard Time (ChST)", cities: [] },
];
